<template>
    <component
        :is="footerComponent"
        data-footer
        :has-footer-logo="!isEmpty(footerLogo)"
        :social-links="socialLinks"
        :entry="footer"
        :data-mode="mode"
        :data-theme="theme"
    >
        <template #logo>
            <NuxtLink
                v-if="footerLogo?.length > 0"
                :to="homeUri"
                aria-label="Home"
            >
                <img
                    :src="footerLogo[0].url"
                    :class="logoSize"
                >
            </NuxtLink>
        </template>

        <template #siteByDigistorm>
            <p class="text-neutral-extra-strong text-sm font-medium">
                site by
                <NuxtLink
                    to="https://www.digistorm.com"
                    class="underline underline-offset-2"
                    target="_blank"
                >
                    Digistorm
                </NuxtLink>
                , a
                <NuxtLink
                    to="https://www.veracross.com"
                    class="underline underline-offset-2"
                    target="_blank"
                >
                    Veracross
                </NuxtLink>
                solution
            </p>
        </template>

        <template #copyright>
            <span class="text-neutral-extra-strong text-sm font-medium">
                © {{ currentYear }} {{ siteDetails?.name }}
            </span>
        </template>
    </component>
</template>

<script setup>
    const { digistormSettings, getGlobalSetField, appId, homeUri, siteDetails } = storeToRefs(useGlobalStore())

    const { colorPalette } = useColorPalette()

    const footer = computed(() => {
        return first(getGlobalSetField.value('ds_Navigation_GlobalSet', 'footer') || [])
    })

    const footerType = computed(() => {
        return digistormSettings.value.footerType
    })

    const footerComponents = import.meta.glob('./**/footer.async.vue')

    // check for any custom components
    const customComponents = await import(`~/custom/${appId.value}/index.js`).catch(() => {
        return null
    })

    const mode = computed(() => {
        if (!footer.value) {
            return colorPalette()
        }

        // get the entry.footer_ColorMode or entry.footer3_ColorMode field depending on footer type
        const colorModeField = Object.keys(footer.value).find((key) => {
            return key.endsWith('_ColorPalette')
        })

        return colorPalette(get(footer.value, colorModeField))
    })

    const theme = computed(() => {
        if (!footer.value) {
            return colorPalette()
        }

        // get the entry.footer_ColorMode or entry.footer3_ColorMode field depending on footer type
        const colorThemeField = Object.keys(footer.value).find((key) => {
            return key.endsWith('_ColorTheme')
        })

        return get(footer.value, colorThemeField)
    })

    const socialLinks = computed(() => {
        return getGlobalSetField.value('ds_Socials_GlobalSet', 'socials_SocialLinks')
    })

    const footerComponent = computed(() => {
        if (footerType.value === 'footer-custom') {
            return customComponents?.default?.Footer
        }

        return defineAsyncComponent(() => {
            const footer = footerComponents[`./${footerType.value}/footer.async.vue`]
            return footer ? footer() : Promise.reject(new Error(`Unknown footer type: ${footerType.value}`))
        })
    })

    const footerLogo = computed(() => {
        const logoType = getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_FooterLogoType', false)

        if (logoType === 'none') {
            return []
        }

        if (logoType === 'custom') {
            return getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_FooterLogo', true)
        }

        if (mode.value === 'brand-invert') {
            return getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_HeaderLogoDark', true)
        }

        return getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_HeaderLogo', true)
    })

    const logoSize = computed(() => {
        const logoType = getGlobalSetField.value('ds_Logos_GlobalSet', 'logos_FooterLogoType', false)
        const sizeField = logoType === 'custom' ? 'logos_FooterLogoSize' : 'logos_HeaderLogoSize'

        switch (getGlobalSetField.value('ds_Logos_GlobalSet', sizeField, true)) {
            case 'sm':
                return 'max-h-18 max-w-48'
            case 'lg':
                return 'max-h-54 max-w-xl'
            case 'md':
            default:
                return 'max-h-36 max-w-96'
        }
    })

    const currentYear = computed(() => {
        return new Date().getFullYear()
    })
</script>
